import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import type Store from 'ember-data/store';
import type Flow from 'garaje/models/flow';
import type Location from 'garaje/models/location';
import type Questionnaire from 'garaje/models/questionnaire';
import type TransitionConfirmService from 'garaje/services/transition-confirm';
import { routeEvent } from 'garaje/utils/decorators/route';
import { TrackedSet } from 'tracked-built-ins';

import type { VisitorsQuestionnairesRouteModel } from '../route';

interface VisitorsQuestionnairesShowRouteParams {
  id: string;
}

export interface VisitorsQuestionnairesShowRouteModel {
  changes: Set<Model>;
  fetchFlowsPage: VisitorsQuestionnairesRouteModel['fetchFlowsPage'];
  flows: Flow[];
  location: Location;
  questionnaire: Questionnaire;
}

export default class VisitorsQuestionnairesShowRoute extends Route {
  @service declare store: Store;
  @service declare transitionConfirm: TransitionConfirmService;

  async model({ id }: VisitorsQuestionnairesShowRouteParams): Promise<VisitorsQuestionnairesShowRouteModel> {
    const { fetchFlowsPage, flows, location } = <VisitorsQuestionnairesRouteModel>(
      this.modelFor('visitors.questionnaires')
    );

    const existingRecord = this.store.peekRecord('questionnaire', id);
    if (existingRecord) {
      existingRecord.unloadRecord();
    }
    const questionnaire = await this.store.findRecord('questionnaire', id, {
      include: [
        'company',
        'flow-questionnaires',
        'flow-questionnaires.flow',
        'flow-questionnaires.flow.location',
        'location',
        'questionnaire-sections',
        'questionnaire-sections.questionnaire-fields',
        'questionnaire-sections.questionnaire-fields.questionnaire-field-options',
      ].join(','),
      reload: true,
    });

    return {
      changes: new TrackedSet<Model>(),
      fetchFlowsPage,
      flows,
      location,
      questionnaire,
    };
  }

  @routeEvent
  routeWillChange(transition: Transition): void {
    const { changes } = <VisitorsQuestionnairesShowRouteModel>this.modelFor(this.routeName);

    // no need to display confirmation modal when nothing is changed
    if (changes.size === 0) return;

    // display confirmation modal if questionnaire is dirty
    void this.transitionConfirm.displayConfirmTask.perform(transition, {
      header: 'There are unsaved changes to this assessment.',
      body: 'You will not be able to recover the changes if you proceed.',
      confirmText: 'Proceed anyway',
      continue() {
        for (const changedModel of changes) {
          changedModel.rollbackAttributes();
        }
        changes.clear();
      },
    });
  }
}
